import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Trash2, Save, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchProducts } from '../../redux/slices/productSlice';
import { fetchSuppliers, createPurchase } from '../../redux/slices/purchaseSlice';
import { updateProductById } from '../../redux/slices/productSlice';
import { addInventoryLog } from '../../services/inventoryLogService';
import { format } from 'date-fns';

const PurchaseDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { products } = useSelector((state) => state.product);
    const { suppliers } = useSelector((state) => state.purchase);

    // Purchase header state
    const [purchaseData, setPurchaseData] = useState({
        supplierName: 'ManiVannan',
        purchaseDate: new Date().toISOString().split('T')[0],
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        purchaseType: 'cash',
    });

    // Purchase items state
    const [purchaseItems, setPurchaseItems] = useState([]);

    // Current row being edited
    const [currentRow, setCurrentRow] = useState({
        productCode: '',
        productName: '',
        productId: '',
        uom: '',
        purchaseRate: '',
        quantity: '1',
        discount: '',
        gst: '',
        landingCost: '',
        valuePoint: 0,
    });

    useEffect(() => {
        dispatch(fetchProducts());
        dispatch(fetchSuppliers());
    }, [dispatch]);

    // Handle product code change
    const handleProductCodeChange = (e) => {
        const code = e.target.value;

        // Find product by code
        const product = products.find(
            (p) => p.productCode?.toLowerCase() === code.toLowerCase()
        );

        if (product) {
            console.log('Product found:', product);
            console.log('Product volPoint:', product.volPoint);

            const vpValue = parseFloat(product.volPoint) || 0;
            console.log('Parsed VP value:', vpValue);

            setCurrentRow({
                ...currentRow,
                productCode: code,
                productName: product.name,
                productId: product.id,
                uom: product.uom || 'PCS',
                valuePoint: vpValue,
            });
        } else {
            setCurrentRow({ ...currentRow, productCode: code });
        }
    };

    // Handle quantity increment/decrement
    const handleQuantityChange = (delta) => {
        const newQuantity = Math.max(1, parseInt(currentRow.quantity || 1) + delta);
        setCurrentRow({ ...currentRow, quantity: newQuantity.toString() });
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentRow({ ...currentRow, [name]: value });
    };

    // Add item to purchase
    const handleAddItem = () => {
        if (!currentRow.productCode || !currentRow.productName) {
            toast.error('Please enter a valid product code');
            return;
        }

        if (!currentRow.landingCost || parseFloat(currentRow.landingCost) <= 0) {
            toast.error('Please enter landing cost');
            return;
        }

        const newItem = {
            ...currentRow,
            id: Date.now().toString(),
        };

        setPurchaseItems([...purchaseItems, newItem]);

        // Reset current row
        setCurrentRow({
            productCode: '',
            productName: '',
            productId: '',
            uom: '',
            purchaseRate: '',
            quantity: '1',
            discount: '',
            gst: '',
            landingCost: '',
            valuePoint: 0,
        });

        toast.success('Item added to purchase');
    };

    // Remove item from purchase
    const handleRemoveItem = (id) => {
        setPurchaseItems(purchaseItems.filter((item) => item.id !== id));
        toast.success('Item removed');
    };

    // Update quantity for existing item
    const handleUpdateItemQuantity = (id, delta) => {
        setPurchaseItems(
            purchaseItems.map((item) => {
                if (item.id === id) {
                    const newQuantity = Math.max(1, parseInt(item.quantity || 1) + delta);
                    return { ...item, quantity: newQuantity.toString() };
                }
                return item;
            })
        );
    };

    // Calculate totals
    const calculateTotals = () => {
        const totalQuantity = purchaseItems.reduce(
            (sum, item) => sum + (parseFloat(item.quantity) || 0),
            0
        );

        const totalAmount = purchaseItems.reduce(
            (sum, item) => sum + (parseFloat(item.landingCost) || 0),
            0
        );

        const totalVolumePoints = purchaseItems.reduce(
            (sum, item) =>
                sum + (parseFloat(item.valuePoint) || 0) * (parseFloat(item.quantity) || 0),
            0
        );

        return { totalQuantity, totalAmount, totalVolumePoints };
    };

    // Handle purchase header change
    const handlePurchaseDataChange = (e) => {
        const { name, value } = e.target;
        setPurchaseData({ ...purchaseData, [name]: value });
    };

    // Submit purchase
    const handleSubmitPurchase = async () => {
        if (!purchaseData.supplierName) {
            toast.error('Please select a supplier');
            return;
        }

        if (!purchaseData.invoiceNumber) {
            toast.error('Please enter invoice number');
            return;
        }

        if (purchaseItems.length === 0) {
            toast.error('Please add at least one item');
            return;
        }

        try {
            const totals = calculateTotals();

            const purchase = {
                ...purchaseData,
                items: purchaseItems.map((item) => ({
                    productId: item.productId,
                    productCode: item.productCode,
                    productName: item.productName,
                    uom: item.uom,
                    purchaseRate: parseFloat(item.purchaseRate) || 0,
                    quantity: parseFloat(item.quantity) || 0,
                    discount: parseFloat(item.discount) || 0,
                    discountAmount: parseFloat(item.discount) || 0,
                    gst: parseFloat(item.gst) || 0,
                    landingCost: parseFloat(item.landingCost) || 0,
                    amount: parseFloat(item.landingCost) || 0,
                    mrp: 0,
                })),
                totalAmount: totals.totalAmount,
                totalVolumePoints: totals.totalVolumePoints,
                inWard: true,
                createdAt: new Date().toISOString(),
            };

            // Create purchase
            await dispatch(createPurchase(purchase)).unwrap();

            // Update product purchase quantities
            const productUpdates = purchaseItems.map(async (item) => {
                const product = products.find((p) => p.id === item.productId);
                if (product) {
                    const currentPurchase = Number(product.purchase) || 0;
                    const newPurchase = currentPurchase + parseFloat(item.quantity);

                    await dispatch(
                        updateProductById({
                            productId: item.productId,
                            productData: {
                                purchase: newPurchase,
                            },
                        })
                    ).unwrap();

                    // Log inventory change
                    await addInventoryLog({
                        productId: item.productId,
                        productName: item.productName || product.name || product.productName,
                        date: new Date().toISOString().split('T')[0],
                        quantityChange: parseFloat(item.quantity) || 0,
                        previousStock: currentPurchase,
                        newStock: newPurchase,
                        transactionType: "Purchase Topup",
                        referenceId: purchaseData.invoiceNumber,
                        customerName: purchaseData.supplierName
                    });
                }
            });

            await Promise.all(productUpdates);

            toast.success('Purchase created successfully!');
            navigate('/purchase-list');
        } catch (error) {
            toast.error('Failed to create purchase: ' + error);
        }
    };

    const totals = calculateTotals();

    return (
        <div className='max-w-full mx-auto px-4 py-5 md:px-6 md:py-6 bg-gray-50 min-h-screen'>
            {/* Header */}
            <div className='mb-4 md:mb-6 flex justify-between items-start'>
                <div className='flex items-center gap-3'>
                    <button
                        onClick={() => navigate('/purchase-list')}
                        className='p-2 hover:bg-gray-100 rounded-lg transition-colors'
                    >
                        <ArrowLeft className='w-5 h-5' />
                    </button>
                    <div>
                        <h1 className='text-[18px] md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 md:mb-2'>
                            New Purchase Entry
                        </h1>
                        <p className='text-[11px] md:text-sm text-gray-600'>
                            Create a new purchase invoice
                        </p>
                    </div>
                </div>
                <button
                    onClick={handleSubmitPurchase}
                    className='flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all font-medium text-[13px]'
                >
                    <Save className='w-4 h-4' />
                    <span>Save Purchase</span>
                </button>
            </div>

            {/* Purchase Header Form */}
            <div className='bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-6 mb-4'>
                <h2 className='text-[15px] font-bold text-gray-900 mb-4'>Purchase Information</h2>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                    <div>
                        <label className='block text-[11px] font-medium text-gray-700 mb-1.5'>
                            Supplier Name
                        </label>
                        <input
                            type='text'
                            value='ManiVannan'
                            readOnly
                            className='w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-[13px] font-semibold text-gray-900'
                        />
                    </div>

                    <div>
                        <label className='block text-[11px] font-medium text-gray-700 mb-1.5'>
                            Purchase Date *
                        </label>
                        <input
                            type='date'
                            name='purchaseDate'
                            value={purchaseData.purchaseDate}
                            onChange={handlePurchaseDataChange}
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
                        />
                    </div>

                    <div>
                        <label className='block text-[11px] font-medium text-gray-700 mb-1.5'>
                            Invoice Number *
                        </label>
                        <input
                            type='text'
                            name='invoiceNumber'
                            value={purchaseData.invoiceNumber}
                            onChange={handlePurchaseDataChange}
                            placeholder='Enter invoice number'
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
                        />
                    </div>

                    <div>
                        <label className='block text-[11px] font-medium text-gray-700 mb-1.5'>
                            Invoice Date
                        </label>
                        <input
                            type='date'
                            name='invoiceDate'
                            value={purchaseData.invoiceDate}
                            onChange={handlePurchaseDataChange}
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
                        />
                    </div>

                    <div>
                        <label className='block text-[11px] font-medium text-gray-700 mb-1.5'>
                            Payment Type *
                        </label>
                        <select
                            name='purchaseType'
                            value={purchaseData.purchaseType}
                            onChange={handlePurchaseDataChange}
                            className='w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-[13px]'
                        >
                            <option value='cash'>Cash</option>
                            <option value='online'>Online</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Purchase Items Table */}
            <div className='bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-4'>
                <div className='p-4 border-b border-gray-200'>
                    <h2 className='text-[15px] font-bold text-gray-900'>Purchase Items</h2>
                </div>

                <div className='overflow-x-auto'>
                    <table className='w-full'>
                        <thead className='bg-gray-50 border-b-2 border-gray-300'>
                            <tr>
                                <th className='px-3 py-3 text-left text-[11px] font-bold text-gray-700 uppercase'>
                                    Product Code
                                </th>
                                <th className='px-3 py-3 text-left text-[11px] font-bold text-gray-700 uppercase'>
                                    Product Name
                                </th>
                                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                                    UOM
                                </th>
                                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                                    Purchase Rate
                                </th>
                                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                                    Quantity
                                </th>
                                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                                    Discount
                                </th>
                                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                                    GST %
                                </th>
                                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                                    Landing Cost
                                </th>
                                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                                    VP Total
                                </th>
                                <th className='px-3 py-3 text-center text-[11px] font-bold text-gray-700 uppercase'>
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Input Row */}
                            <tr className='bg-blue-50 border-b-2 border-blue-200'>
                                <td className='px-3 py-2'>
                                    <input
                                        type='text'
                                        value={currentRow.productCode}
                                        onChange={handleProductCodeChange}
                                        placeholder='Enter code'
                                        className='w-full px-2 py-1.5 border border-gray-300 rounded text-[13px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                                    />
                                </td>
                                <td className='px-3 py-2'>
                                    <input
                                        type='text'
                                        value={currentRow.productName}
                                        readOnly
                                        placeholder='Auto-filled'
                                        className='w-full px-2 py-1.5 border border-gray-200 rounded text-[13px] bg-gray-50'
                                    />
                                </td>
                                <td className='px-3 py-2'>
                                    <input
                                        type='text'
                                        value={currentRow.uom}
                                        readOnly
                                        className='w-20 px-2 py-1.5 border border-gray-200 rounded text-[13px] text-center bg-gray-50'
                                    />
                                </td>
                                <td className='px-3 py-2'>
                                    <input
                                        type='number'
                                        name='purchaseRate'
                                        value={currentRow.purchaseRate}
                                        onChange={handleInputChange}
                                        placeholder='0.00'
                                        step='0.01'
                                        className='w-24 px-2 py-1.5 border border-gray-300 rounded text-[13px] text-center focus:ring-2 focus:ring-blue-500'
                                    />
                                </td>
                                <td className='px-3 py-2'>
                                    <div className='flex items-center gap-1'>
                                        <button
                                            onClick={() => handleQuantityChange(-1)}
                                            className='p-1 bg-red-100 hover:bg-red-200 rounded transition-colors'
                                        >
                                            <Minus className='w-3 h-3 text-red-600' />
                                        </button>
                                        <input
                                            type='number'
                                            value={currentRow.quantity}
                                            onChange={(e) =>
                                                setCurrentRow({ ...currentRow, quantity: e.target.value })
                                            }
                                            className='w-16 px-2 py-1.5 border border-gray-300 rounded text-[13px] text-center focus:ring-2 focus:ring-blue-500'
                                        />
                                        <button
                                            onClick={() => handleQuantityChange(1)}
                                            className='p-1 bg-green-100 hover:bg-green-200 rounded transition-colors'
                                        >
                                            <Plus className='w-3 h-3 text-green-600' />
                                        </button>
                                    </div>
                                </td>
                                <td className='px-3 py-2'>
                                    <input
                                        type='number'
                                        name='discount'
                                        value={currentRow.discount}
                                        onChange={handleInputChange}
                                        placeholder='0.00'
                                        step='0.01'
                                        className='w-24 px-2 py-1.5 border border-gray-300 rounded text-[13px] text-center focus:ring-2 focus:ring-blue-500'
                                    />
                                </td>
                                <td className='px-3 py-2'>
                                    <input
                                        type='number'
                                        name='gst'
                                        value={currentRow.gst}
                                        onChange={handleInputChange}
                                        placeholder='0'
                                        step='0.01'
                                        className='w-20 px-2 py-1.5 border border-gray-300 rounded text-[13px] text-center focus:ring-2 focus:ring-blue-500'
                                    />
                                </td>
                                <td className='px-3 py-2'>
                                    <input
                                        type='number'
                                        name='landingCost'
                                        value={currentRow.landingCost}
                                        onChange={handleInputChange}
                                        placeholder='0.00'
                                        step='0.01'
                                        className='w-28 px-2 py-1.5 border border-gray-300 rounded text-[13px] text-center font-semibold focus:ring-2 focus:ring-blue-500'
                                    />
                                </td>
                                <td className='px-3 py-2 text-center text-[13px] font-semibold text-purple-700'>
                                    {((currentRow.valuePoint || 0) * (parseFloat(currentRow.quantity) || 0)).toFixed(
                                        2
                                    )}
                                </td>
                                <td className='px-3 py-2 text-center'>
                                    <button
                                        onClick={handleAddItem}
                                        className='p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors'
                                        title='Add Item'
                                    >
                                        <Plus className='w-4 h-4' />
                                    </button>
                                </td>
                            </tr>

                            {/* Added Items */}
                            {purchaseItems.map((item) => (
                                <tr key={item.id} className='hover:bg-gray-50 border-b border-gray-100'>
                                    <td className='px-3 py-2 text-[13px] text-gray-900'>{item.productCode}</td>
                                    <td className='px-3 py-2 text-[13px] text-gray-900 font-medium'>
                                        {item.productName}
                                    </td>
                                    <td className='px-3 py-2 text-center text-[13px] text-gray-900 uppercase'>
                                        {item.uom}
                                    </td>
                                    <td className='px-3 py-2 text-center text-[13px] text-gray-900'>
                                        {parseFloat(item.purchaseRate || 0).toFixed(2)}
                                    </td>
                                    <td className='px-3 py-2'>
                                        <div className='flex items-center justify-center gap-1'>
                                            <button
                                                onClick={() => handleUpdateItemQuantity(item.id, -1)}
                                                className='p-1 bg-red-100 hover:bg-red-200 rounded transition-colors'
                                            >
                                                <Minus className='w-3 h-3 text-red-600' />
                                            </button>
                                            <span className='w-16 text-center text-[13px] font-semibold'>
                                                {item.quantity}
                                            </span>
                                            <button
                                                onClick={() => handleUpdateItemQuantity(item.id, 1)}
                                                className='p-1 bg-green-100 hover:bg-green-200 rounded transition-colors'
                                            >
                                                <Plus className='w-3 h-3 text-green-600' />
                                            </button>
                                        </div>
                                    </td>
                                    <td className='px-3 py-2 text-center text-[13px] text-gray-900'>
                                        {parseFloat(item.discount || 0).toFixed(2)}
                                    </td>
                                    <td className='px-3 py-2 text-center text-[13px] text-gray-900'>
                                        {parseFloat(item.gst || 0).toFixed(2)}%
                                    </td>
                                    <td className='px-3 py-2 text-center text-[13px] font-bold text-green-700'>
                                        ₹{parseFloat(item.landingCost || 0).toFixed(2)}
                                    </td>
                                    <td className='px-3 py-2 text-center text-[13px] font-semibold text-purple-700'>
                                        {((item.valuePoint || 0) * (parseFloat(item.quantity) || 0)).toFixed(2)}
                                    </td>
                                    <td className='px-3 py-2 text-center'>
                                        <button
                                            onClick={() => handleRemoveItem(item.id)}
                                            className='p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                                            title='Remove'
                                        >
                                            <Trash2 className='w-4 h-4' />
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {/* Totals Row */}
                            {purchaseItems.length > 0 && (
                                <tr className='bg-gray-100 border-t-2 border-gray-400 font-bold'>
                                    <td colSpan='4' className='px-3 py-3 text-right text-[13px] font-bold text-gray-900'>
                                        TOTALS
                                    </td>
                                    <td className='px-3 py-3 text-center text-[13px] font-bold text-blue-700'>
                                        {totals.totalQuantity.toFixed(2)}
                                    </td>
                                    <td colSpan='2' className='px-3 py-3'></td>
                                    <td className='px-3 py-3 text-center text-[15px] font-bold text-green-700'>
                                        ₹{totals.totalAmount.toLocaleString()}
                                    </td>
                                    <td className='px-3 py-3 text-center text-[15px] font-bold text-purple-700'>
                                        {totals.totalVolumePoints.toLocaleString()}
                                    </td>
                                    <td></td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {purchaseItems.length === 0 && (
                    <div className='px-6 py-12 text-center text-gray-500 text-sm'>
                        No items added yet. Enter a product code above to start adding items.
                    </div>
                )}
            </div>

            {/* Summary Cards */}
            {purchaseItems.length > 0 && (
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                    <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4'>
                        <p className='text-[11px] text-gray-600 mb-1'>Total Quantity</p>
                        <p className='text-2xl font-bold text-blue-700'>{totals.totalQuantity.toFixed(2)}</p>
                    </div>
                    <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4'>
                        <p className='text-[11px] text-gray-600 mb-1'>Total Amount</p>
                        <p className='text-2xl font-bold text-green-700'>
                            ₹{totals.totalAmount.toLocaleString()}
                        </p>
                    </div>
                    <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4'>
                        <p className='text-[11px] text-gray-600 mb-1'>Total Volume Points</p>
                        <p className='text-2xl font-bold text-purple-700'>
                            {totals.totalVolumePoints.toLocaleString()}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseDetails;
